import { api } from '@/lib/api';

/**
 * What the shop front was seen to do, batched and sent once.
 *
 * ── WHY NOT A REQUEST PER SLIDE ─────────────────────────────────────────────
 *
 * Two carousels turning every six seconds is a request every three seconds,
 * for as long as somebody leaves the tab open. On a Nigerian mobile connection
 * that is real money out of a buyer's data bundle to tell ChiCare something
 * nobody is reading in real time.
 *
 * So impressions accumulate in a Set and go out when the page is actually being
 * left: `visibilitychange` to hidden, which fires on tab switch, on app
 * backgrounding and on navigation away, and is the one event mobile browsers
 * are reliable about. `beforeunload` is deliberately not used — Safari on iOS
 * frequently never fires it.
 *
 * ── WHY sendBeacon ──────────────────────────────────────────────────────────
 *
 * A normal fetch issued while the page is unloading is cancelled by the
 * browser, which is why analytics written that way quietly undercounts. A
 * beacon is handed to the browser to deliver after the page is gone. It cannot
 * carry our bearer token, which is fine: this endpoint is public, because the
 * shop front is public.
 *
 * ── WHY LOSING SOME IS ACCEPTABLE ───────────────────────────────────────────
 *
 * These counts answer "is this slide doing anything" for the person who made
 * it. Nothing is priced or invoiced from them. A paid sponsorship must never be
 * counted this way — see BannerTrackController for why.
 */

const seen = new Set<number>();
const clicked = new Set<number>();

let listening = false;

function endpoint(): string {
  // The same base the client uses, so a deploy that moves the API does not
  // leave this pointing at the old one.
  return `${api.apiBase}/banners/track`;
}

function flush(): void {
  if (seen.size === 0 && clicked.size === 0) {
    return;
  }

  const body = JSON.stringify({
    // Capped to match the server, which refuses more. A tab left open all day
    // on a long carousel would otherwise build a list it will not accept.
    impressions: [...seen].slice(0, 60),
    clicks: [...clicked].slice(0, 20),
  });

  seen.clear();
  clicked.clear();

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(endpoint(), new Blob([body], { type: 'application/json' }));

      return;
    }

    void fetch(endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Counting a banner is never allowed to break a shop front. Swallowed on
    // purpose, and not retried: the next flush carries whatever came after.
  }
}

function listen(): void {
  if (listening || typeof document === 'undefined') {
    return;
  }

  listening = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });

  // A single-page app can go a whole session without the document ever being
  // hidden. This is the backstop, long enough that a browsing session produces
  // one or two requests rather than hundreds.
  window.setInterval(flush, 60_000);
}

export const trackBanners = {
  seen(id: number): void {
    listen();
    seen.add(id);
  },

  clicked(id: number): void {
    listen();
    clicked.add(id);
    // Sent immediately. A click is usually followed by a navigation, and a
    // click that only lands if the person later backgrounds the tab is a click
    // that mostly does not land.
    flush();
  },

  /** For tests, which must not leak counts between cases. */
  reset(): void {
    seen.clear();
    clicked.clear();
  },
};
